<template>
  <div class="budget-management">
    <h2>Gestione Budget Attività</h2>
    
    <!-- Alerts -->
    <div v-if="alert.show" :class="`alert alert-${alert.type}`" class="alert-dismissible">
      {{ alert.message }}
      <button @click="alert.show = false" class="btn-close"></button>
    </div>
    
    <!-- Header con controlli -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">Filtri</h5>
            <div class="row g-3">
              <!-- Filtro Anno -->
              <div class="col-md-3">
                <label for="filterYear" class="form-label">Anno</label>
                <select 
                  id="filterYear" 
                  v-model="filters.year" 
                  class="form-select"
                >
                  <option value="">Tutti</option>
                  <option v-for="year in availableYears" :key="year" :value="year">
                    {{ year }}
                  </option>
                </select>
              </div>
              
              <!-- Filtro Tipo -->
              <div class="col-md-3">
                <label for="filterTipo" class="form-label">Tipo</label>
                <select 
                  id="filterTipo" 
                  v-model="filters.tipo" 
                  class="form-select"
                >
                  <option value="">Tutti</option>
                  <option value="0">Previsto</option>
                  <option value="10">Consolidato</option>
                  <option value="20">Fatturato</option>
                </select>
              </div>
              
              <!-- Bottone Reset -->
              <div class="col-md-3 d-flex align-items-end">
                <button @click="resetFilters" class="btn btn-outline-secondary me-2">
                  <i class="fas fa-times"></i> Reset
                </button>
                <button @click="loadBudgets" class="btn btn-outline-primary">
                  <i class="fas fa-refresh"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-md-4">
        <div class="d-flex justify-content-end align-items-start h-100">
          <button 
            class="btn btn-primary btn-lg"
            @click="openNewBudgetDialog"
            title="Aggiungi nuovo budget attività"
          >
            <i class="fas fa-plus"></i> Nuovo Budget
          </button>
        </div>
      </div>
    </div>

    <!-- Statistiche veloci -->
    <div class="row mb-4" v-if="filteredBudgets.length > 0">
      <div class="col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <h5 class="text-primary">{{ stats.total }}</h5>
            <small class="text-muted">Totale Attività</small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <h5 class="text-primary">{{ budgetAPI.formatCurrency(stats.totalBudget) }}</h5>
            <small class="text-muted">Budget Totale</small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <h5 class="text-warning">{{ budgetAPI.formatCurrency(stats.consolidato) }}</h5>
            <small class="text-muted">Consolidato</small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <h5 class="text-success">{{ budgetAPI.formatCurrency(stats.fatturato) }}</h5>
            <small class="text-muted">Fatturato</small>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Tabella Budget -->
    <div v-if="isLoading && budgets.length === 0" class="card">
      <div class="card-body text-center py-4">
        <div class="spinner-border"></div>
        <p class="mt-2">Caricamento budget...</p>
      </div>
    </div>
    
    <div v-else-if="filteredBudgets.length === 0" class="card">
      <div class="card-body text-center py-4 text-muted">
        <i class="fas fa-chart-bar fa-3x mb-3"></i>
        <p>Nessun budget trovato con i filtri selezionati.</p>
        <p>Clicca su "Nuovo Budget" per creare il primo.</p>
      </div>
    </div>
    
    <TableSortable v-else :data="filteredBudgets" :options="budgetsTableOptions">
      <template #field-person_name="{ row }">
        <strong>{{ row.person_name }}</strong>
      </template>
      
      <template #field-project_name="{ row }">
        {{ row.project_name }}
      </template>
      
      <template #field-year="{ row }">
        <span class="badge bg-info">{{ row.year }}</span>
      </template>
      
      <template #field-tipo_label="{ row }">
        <span :class="budgetAPI.getTipoClass(row.tipo)">
          {{ row.tipo_label }}
        </span>
      </template>
      
      <template #field-budget="{ row }">
        <strong>{{ budgetAPI.formatCurrency(row.budget) }}</strong>
      </template>
      
      <template #field-note="{ row }">
        <span 
          v-if="row.note" 
          :title="row.note"
          class="text-truncate d-inline-block"
          style="max-width: 150px;"
        >
          {{ row.note }}
        </span>
        <span v-else class="text-muted">-</span>
      </template>
      
      <template #field-actions="{ row }">
        <div class="btn-group">
          <button 
            @click="openEditBudgetDialog(row)"
            class="btn btn-sm btn-outline-primary"
            title="Modifica"
          >
            <i class="fas fa-edit"></i>
          </button>
          <button 
            @click="deleteBudget(row.id_budget, `${row.person_name} - ${row.project_name} (${row.year})`)"
            class="btn btn-sm btn-outline-danger"
            title="Elimina"
          >
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </template>
    </TableSortable>

    <!-- Dialog Modal per Budget -->
    <BudgetDialog
      v-model="showDialog"
      :budget="editingBudget"
      :dbmng-schema="dbmngSchema"
      @saved="onBudgetSaved"
    />
  </div>
</template>

<script>
import budgetAPI from '@/services/budgetAPI.js'
import TableSortable from '@/components/TableSortable.vue'
import BudgetDialog from '@/components/BudgetDialog.vue'

export default {
  name: 'BudgetManagement',
  components: {
    TableSortable,
    BudgetDialog
  },
  data() {
    return {
      budgetAPI, // Espone l'API per il template
      budgets: [],
      dbmngSchema: null,
      isLoading: false,
      alert: {
        show: false,
        type: 'success',
        message: ''
      },
      filters: {
        year: new Date().getFullYear(),
        tipo: ''
      },
      // Dialog
      showDialog: false,
      editingBudget: null
    }
  },

  computed: {
    availableYears() {
      const currentYear = new Date().getFullYear()
      return Array.from({ length: 11 }, (_, i) => currentYear - 5 + i)
    },

    stats() {
      const data = this.filteredBudgets
      if (!data.length) return { total: 0, totalBudget: 0, consolidato: 0, fatturato: 0 }
      
      return {
        total: data.length,
        totalBudget: data.reduce((sum, b) => sum + (parseFloat(b.budget) || 0), 0),
        consolidato: data.filter(b => b.tipo === 10).reduce((sum, b) => sum + (parseFloat(b.budget) || 0), 0),
        fatturato: data.filter(b => b.tipo === 20).reduce((sum, b) => sum + (parseFloat(b.budget) || 0), 0)
      }
    },
    
    // Filtra i budget in base ai filtri selezionati
    filteredBudgets() {
      let filtered = this.budgets
      
      // Filtro anno
      if (this.filters.year) {
        filtered = filtered.filter(b => b.year == this.filters.year)
      }
      
      // Filtro tipo
      if (this.filters.tipo !== '') {
        filtered = filtered.filter(b => b.tipo == this.filters.tipo)
      }
      
      return filtered
    },
    
    budgetsTableOptions() {
      return {
        structure: [
          { key: 'person_name', label: 'Persona', sortable: true, filterable: true },
          { key: 'project_name', label: 'Progetto', sortable: true, filterable: true },
          { key: 'year', label: 'Anno', sortable: true },
          { key: 'tipo_label', label: 'Tipo', sortable: true },
          { key: 'budget', label: 'Budget', sortable: true, align: 'right' },
          { key: 'note', label: 'Note', sortable: false },
          { key: 'actions', label: 'Azioni', sortable: false, align: 'right' }
        ]
      }
    }
  },

  async mounted() {
    await this.loadBudgets()
  },

  methods: {
    async loadBudgets() {
      try {
        this.isLoading = true
        
        // Carica tutti i budget, filtreremo client-side
        const result = await budgetAPI.getAll()
        
        if (result.ok) {
          this.budgets = result.data || []
          this.dbmngSchema = result.dbmng


          // Popola le select con i dati dal backend
          this.peopleOptions = result.people || []
          this.projectOptions = result.projects || []
          
          this.showAlert('success', `Caricati ${this.filteredBudgets.length} budget`)
        } else {
          this.showAlert('danger', result.message || 'Errore nel caricamento budget')
        }
      } catch (error) {
        this.showAlert('danger', error.message || 'Errore di connessione')
      } finally {
        this.isLoading = false
      }
    },

    resetFilters() {
      this.filters = {
        year: new Date().getFullYear(),
        tipo: ''
      }
      this.loadBudgets()
    },

    openNewBudgetDialog() {
      this.editingBudget = null
      this.showDialog = true
    },

    openEditBudgetDialog(budget) {
      this.editingBudget = budget
      this.showDialog = true
    },

    onBudgetSaved(result) {
      this.showAlert('success', result.message || 'Budget salvato con successo')
      this.loadBudgets()
    },

    async deleteBudget(budgetId, description) {
      if (!confirm(`Sei sicuro di voler eliminare il budget per:\n${description}?`)) {
        return
      }
      
      try {
        const result = await budgetAPI.delete(budgetId)
        
        if (result.ok) {
          this.showAlert('success', result.message)
          this.loadBudgets()
        } else {
          this.showAlert('danger', result.message)
        }
      } catch (error) {
        this.showAlert('danger', error.message || 'Errore di connessione')
      }
    },

    showAlert(type, message) {
      this.alert = { show: true, type, message }
      setTimeout(() => { this.alert.show = false }, 5000)
    }
  }
}
</script>

<style scoped>
.budget-management {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.table th {
  background-color: #f8f9fa;
  border-top: none;
  font-weight: 600;
}

.table td {
  vertical-align: middle;
}

.badge {
  font-size: 0.8em;
}

.text-truncate {
  cursor: help;
}

/* Dialog Modal Styles */
.modal.show {
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-dialog {
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-content {
  border: none;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}

.modal-header {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

.modal-title {
  font-weight: 600;
  color: #495057;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  font-weight: bold;
  color: #000;
  opacity: 0.5;
  cursor: pointer;
}

.btn-close:hover {
  opacity: 0.8;
}

.btn-close:before {
  content: '×';
}

@media (max-width: 768px) {
  .budget-management {
    padding: 10px;
  }
  
  .table-responsive {
    font-size: 0.875rem;
  }
}
</style>